import { useEffect, useState } from "react";
import type { ApiClient } from "../../../api/api-client";
import { miningDifficultySchema } from "../../../api/schemas";

/** Public display only; an issued challenge always owns the worker's target. */
export function useMiningDifficulty(api: ApiClient, enabled: boolean, context: string,
  readContext: () => string, instanceId: string | undefined, refreshRevision: number): string | null {
  const [stored, setStored] = useState<{ context: string; bits: string } | null>(null);
  useEffect(() => {
    setStored(null);
    if (!enabled) return;
    const abort = new AbortController();
    let pending = false;
    const current = () => !abort.signal.aborted && context === readContext();
    const load = () => {
      if (!current() || pending || document.visibilityState === "hidden") return;
      pending = true;
      api.get("/mining/difficulty", miningDifficultySchema, abort.signal).then(data => {
        if (!current()) return;
        const matches = !instanceId || data.instanceId.toLowerCase() === instanceId.toLowerCase();
        setStored(matches ? { context, bits: data.difficultyBits } : null);
      }).catch(() => { if (current()) setStored(null); }).finally(() => { pending = false; });
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") setStored(null);
      else load();
    };
    load();
    const timer = setInterval(load, 30_000);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      abort.abort(); clearInterval(timer);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [api, enabled, context, readContext, instanceId, refreshRevision]);
  return enabled && stored?.context === context ? stored.bits : null;
}
